import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';

import { RequireAuth } from '@/app/auth/RequireAuth';
import { AuthenticationPageLayout } from '@/app/shell/AuthenticationPageLayout';
import { NotFoundPage } from '@/app/shell/NotFoundPage';
import { incidentDetailRoute } from '@/features/shared/routes';
import { activateLiveVideo as requestLiveVideoActivation } from '@ngsos/portal-core/commands';
import { reportUnhandledError } from '@/lib/errors/unhandledErrors';
import { parseLegacyIncidentId } from './legacyUrls';

// Module-level so React StrictMode's doubled effects never activate the video twice.
const activations = new Map<string, Promise<void>>();

async function activateLiveVideo(incidentId: string): Promise<void> {
  const response = await requestLiveVideoActivation(incidentId);

  if (!response.ok) {
    throw new Error(`Activate live video failed (HTTP ${response.status})`);
  }
}

function ActivateLiveVideoAndRedirect({ incidentId }: { incidentId: string }) {
  const { t } = useTranslation();
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    let stillOnPage = true;

    const activation = activations.get(incidentId) ?? activateLiveVideo(incidentId);
    activations.set(incidentId, activation);

    activation
      // The operator lands on the incident either way; the error bar tells them the video did not start.
      .catch(reportUnhandledError)
      .finally(() => {
        if (stillOnPage) {
          setActivated(true);
        }
      });

    return () => {
      stillOnPage = false;
    };
  }, [incidentId]);

  if (activated) {
    return <Navigate to={incidentDetailRoute(incidentId)} replace />;
  }

  return (
    <AuthenticationPageLayout>
      <p className="fade-in-delay">{t('Incident_Video_StartingVideoStream')}</p>
    </AuthenticationPageLayout>
  );
}

export function LegacyStartVideoChatRedirect() {
  const { id } = useParams();
  const incidentId = parseLegacyIncidentId(id);

  if (incidentId === null) {
    return <NotFoundPage />;
  }

  return (
    <RequireAuth>
      <ActivateLiveVideoAndRedirect incidentId={incidentId} />
    </RequireAuth>
  );
}
